import { User } from 'lucide-react'
import React, { useState } from 'react'

interface AvatarProps {
  src?: string
  alt?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  name?: string
  className?: string
  fallbackColor?: string
}

const Avatar: React.FC<AvatarProps> = ({
  src,
  alt = '头像',
  size = 'md',
  name,
  className = '',
  fallbackColor = 'bg-gray-200'
}) => {
  const [imageError, setImageError] = useState(false)

  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
    xl: 'w-12 h-12 text-lg'
  }

  const iconSizes = {
    sm: 6,
    md: 8,
    lg: 10,
    xl: 12
  }

  const getInitials = (name: string) => {
    if (!name) return ''
    const words = name.trim().split(/\s+/)
    if (words.length === 1) {
      return words[0].charAt(0).toUpperCase()
    }
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('')
  }

  const getColorFromName = (name: string) => {
    if (!name) return 'bg-gray-200'

    const colors = [
      'bg-red-200 text-red-800',
      'bg-blue-200 text-blue-800',
      'bg-green-200 text-green-800',
      'bg-yellow-200 text-yellow-800',
      'bg-purple-200 text-purple-800',
      'bg-pink-200 text-pink-800',
      'bg-indigo-200 text-indigo-800',
      'bg-orange-200 text-orange-800'
    ]

    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }

    return colors[Math.abs(hash) % colors.length]
  }

  const baseClasses = `
    ${sizeClasses[size]} 
    rounded-full 
    flex 
    items-center 
    justify-center 
    font-medium 
    select-none
    transition-all
    duration-200
    ${className}
  `
    .replace(/\s+/g, ' ')
    .trim()

  const handleImageError = () => {
    setImageError(true)
  }

  if (src && !imageError) {
    return (
      <div className={baseClasses}>
        <img
          src={src}
          alt={alt}
          className="w-full h-full rounded-full object-cover"
          onError={handleImageError}
          loading="lazy"
        />
      </div>
    )
  }

  if (name) {
    const colorClass = getColorFromName(name)
    return (
      <div className={`${baseClasses} ${colorClass}`}>{getInitials(name)}</div>
    )
  }

  return (
    <div className={`${baseClasses} ${fallbackColor} text-gray-600`}>
      <User size={iconSizes[size]} />
    </div>
  )
}

export default Avatar
